export type AlmightyReferenceKind = 'image' | 'video' | 'audio'

export type AlmightyReferenceItem = {
    id: string
    kind: AlmightyReferenceKind
    url?: string
    name?: string
    duration?: number
    loading?: boolean
}

export const ALMIGHTY_IMAGE_MAX_COUNT = 9
export const ALMIGHTY_VIDEO_MAX_COUNT = 3
export const ALMIGHTY_AUDIO_MAX_COUNT = 3
export const ALMIGHTY_VIDEO_MIN_DURATION = 2
export const ALMIGHTY_VIDEO_MAX_DURATION = 15
export const ALMIGHTY_VIDEO_TOTAL_MAX_DURATION = 15
export const ALMIGHTY_AUDIO_MIN_DURATION = 2
export const ALMIGHTY_AUDIO_MAX_DURATION = 15
export const ALMIGHTY_AUDIO_TOTAL_MAX_DURATION = 15
export const DANCE_25_IMAGE_MAX_COUNT = 30
export const DANCE_25_VIDEO_MAX_COUNT = 10
export const DANCE_25_AUDIO_MAX_COUNT = 10
export const DANCE_25_IMAGE_MAX_SIZE = 30 * 1024 * 1024
export const DANCE_25_VIDEO_MAX_SIZE = 200 * 1024 * 1024
export const DANCE_25_AUDIO_MAX_SIZE = 15 * 1024 * 1024
export const DANCE_25_MEDIA_MIN_DURATION = 2
export const DANCE_25_MEDIA_MAX_DURATION = 30
export const DANCE_25_MEDIA_TOTAL_MAX_DURATION = 30
export const DANCE_25_MEDIA_DURATION_LIMITS = {
    minDuration: DANCE_25_MEDIA_MIN_DURATION,
    maxDuration: DANCE_25_MEDIA_MAX_DURATION,
    totalMaxDuration: DANCE_25_MEDIA_TOTAL_MAX_DURATION,
} as const
export const DANCE_25_VIDEO_MIN_SIDE = 480
export const DANCE_25_VIDEO_MAX_SIDE = 4096
export const ALMIGHTY_MEDIA_METADATA_TIMEOUT = 10000
export const ALMIGHTY_IMAGE_SIZE_LIMIT = 100 * 1024 * 1024
export const ALMIGHTY_IMAGE_RESOLUTION_LIMIT = 4096

export type AlmightyValidationIssue =
    | 'image-max-count'
    | 'video-max-count'
    | 'video-duration-range'
    | 'video-total-duration'
    | 'audio-max-count'
    | 'audio-duration-range'
    | 'audio-total-duration'
    | 'audio-needs-visual-reference'
    | 'reference-required'

type AlmightyValidationMessageContext = {
    duration?: number
    totalDuration?: number
    imageMaxCount?: number
    videoMaxCount?: number
    audioMaxCount?: number
    minDuration?: number
    maxDuration?: number
    totalMaxDuration?: number
}

const DANCE_25_FILE_EXTENSIONS: Record<AlmightyReferenceKind, Set<string>> = {
    image: new Set([
        'jpeg',
        'jpg',
        'png',
        'webp',
        'bmp',
        'tif',
        'tiff',
        'gif',
        'heic',
        'heif',
    ]),
    video: new Set(['mp4', 'mov']),
    audio: new Set(['mp3', 'wav']),
}

const DANCE_25_MIME_TYPES: Record<AlmightyReferenceKind, Set<string>> = {
    image: new Set([
        'image/jpeg',
        'image/png',
        'image/webp',
        'image/bmp',
        'image/x-ms-bmp',
        'image/tiff',
        'image/gif',
        'image/heic',
        'image/heif',
    ]),
    video: new Set(['video/mp4', 'video/quicktime']),
    audio: new Set([
        'audio/mpeg',
        'audio/mp3',
        'audio/wav',
        'audio/x-wav',
        'audio/wave',
    ]),
}

const getFileExtension = (file: File) => {
    const name = String(file?.name || '').toLowerCase()
    const index = name.lastIndexOf('.')
    return index >= 0 ? name.slice(index + 1) : ''
}

const getDance25FileSizeLimit = (kind: AlmightyReferenceKind) => {
    if (kind === 'image') return DANCE_25_IMAGE_MAX_SIZE
    if (kind === 'video') return DANCE_25_VIDEO_MAX_SIZE
    return DANCE_25_AUDIO_MAX_SIZE
}

export const getDance25FileSizeLabel = (kind: AlmightyReferenceKind) => {
    if (kind === 'image') return '30MB'
    if (kind === 'video') return '200MB'
    return '15MB'
}

export const validateDance25ReferenceFile = (
    file: File,
    kind: AlmightyReferenceKind
): 'unsupported-type' | 'file-too-large' | null => {
    const mimeType = String(file?.type || '').toLowerCase()
    const extension = getFileExtension(file)
    const hasSupportedExtension = DANCE_25_FILE_EXTENSIONS[kind].has(extension)
    const hasSupportedMimeType = DANCE_25_MIME_TYPES[kind].has(mimeType)
    const hasKnownMimeType = !!mimeType && mimeType !== 'application/octet-stream'
    if (
        (extension && !hasSupportedExtension) ||
        (hasKnownMimeType && !hasSupportedMimeType) ||
        (!hasSupportedExtension && !hasSupportedMimeType)
    ) {
        return 'unsupported-type'
    }
    return file.size > getDance25FileSizeLimit(kind)
        ? 'file-too-large'
        : null
}

export const isDance25VideoResolutionValid = ({
    width,
    height,
}: {
    width: number
    height: number
}) => {
    if (!Number.isFinite(width) || !Number.isFinite(height)) return false
    return (
        Math.min(width, height) >= DANCE_25_VIDEO_MIN_SIDE &&
        Math.max(width, height) <= DANCE_25_VIDEO_MAX_SIDE
    )
}

type AlmightyMediaReadErrorCode =
    | 'video-read-timeout'
    | 'video-invalid-duration'
    | 'video-read-failed'
    | 'audio-read-timeout'
    | 'audio-invalid-duration'
    | 'audio-read-failed'

export class AlmightyMediaReadError extends Error {
    code: AlmightyMediaReadErrorCode

    constructor(code: AlmightyMediaReadErrorCode) {
        super(code)
        this.name = 'AlmightyMediaReadError'
        this.code = code
    }
}

const isZhLocale = (locale?: string) => {
    return typeof locale === 'string' && locale.toLowerCase().startsWith('zh')
}

const normalizeFiniteDuration = (duration: number) => {
    if (!Number.isFinite(duration) || duration <= 0) return 0
    return Number(duration.toFixed(3))
}

const isFinitePositiveDuration = (duration: number) => {
    return Number.isFinite(duration) && duration > 0
}

const createMediaSource = (source: File | string) => {
    if (typeof source === 'string') {
        return {
            src: source,
            revoke: () => {},
        }
    }

    const src = URL.createObjectURL(source)
    return {
        src,
        revoke: () => {
            URL.revokeObjectURL(src)
        },
    }
}

const cleanupMediaElement = (
    media: HTMLMediaElement,
    revoke: () => void
) => {
    revoke()
    media.onloadedmetadata = null
    media.onerror = null
    media.ondurationchange = null
    media.onloadeddata = null
    media.onseeked = null
    try {
        media.pause()
    } catch {
        // ignore
    }
    try {
        media.removeAttribute('src')
        media.load()
    } catch {
        // ignore
    }
    try {
        media.remove()
    } catch {
        // ignore
    }
}

export const getAlmightyFileKind = (
    file: File,
    allowExtensionFallback = false
): AlmightyReferenceKind | 'other' => {
    const type = (file?.type || '').toLowerCase()
    if (type.startsWith('image/')) return 'image'
    if (type.startsWith('video/')) return 'video'
    if (type.startsWith('audio/')) return 'audio'
    if (!allowExtensionFallback) return 'other'
    const extension = getFileExtension(file)
    if (DANCE_25_FILE_EXTENSIONS.image.has(extension)) return 'image'
    if (DANCE_25_FILE_EXTENSIONS.video.has(extension)) return 'video'
    if (DANCE_25_FILE_EXTENSIONS.audio.has(extension)) return 'audio'
    return 'other'
}

export const isSupportedAlmightyImageFile = (file: File) => {
    return (file?.type || '').toLowerCase().startsWith('image/')
}

export const getImageResolution = async (
    file: File
): Promise<{ width: number; height: number }> => {
    if (!import.meta.client) {
        return { width: 0, height: 0 }
    }

    return await new Promise((resolve, reject) => {
        const img = new Image()
        const src = URL.createObjectURL(file)
        img.onload = () => {
            resolve({
                width: Number(img.width) || 0,
                height: Number(img.height) || 0,
            })
            URL.revokeObjectURL(src)
        }
        img.onerror = () => {
            URL.revokeObjectURL(src)
            reject(new Error('image-read-failed'))
        }
        img.src = src
    })
}

export const isResolutionWithinLimit = (
    resolution: { width: number; height: number },
    limit = ALMIGHTY_IMAGE_RESOLUTION_LIMIT
) => {
    return resolution.width <= limit && resolution.height <= limit
}

export const formatAlmightyDurationSeconds = (duration?: number) => {
    const safeDuration = Number(duration || 0)
    if (!Number.isFinite(safeDuration) || safeDuration <= 0) return ''
    const rounded = Number(safeDuration.toFixed(1))
    return rounded > 0 ? `${rounded}s` : ''
}

export const toAlmightyRequestDuration = (duration?: number) => {
    return Math.max(0, Math.ceil(Number(duration || 0)))
}

export const validateAlmightyImageCount = (
    nextCount: number,
    maxCount = ALMIGHTY_IMAGE_MAX_COUNT
) => {
    return nextCount > maxCount ? 'image-max-count' : null
}

export const validateAlmightyVideoLimits = ({
    nextCount,
    duration,
    nextTotalDuration,
    maxCount = ALMIGHTY_VIDEO_MAX_COUNT,
    minDuration = ALMIGHTY_VIDEO_MIN_DURATION,
    maxDuration = ALMIGHTY_VIDEO_MAX_DURATION,
    totalMaxDuration = ALMIGHTY_VIDEO_TOTAL_MAX_DURATION,
}: {
    nextCount: number
    duration?: number
    nextTotalDuration?: number
    maxCount?: number
    minDuration?: number
    maxDuration?: number
    totalMaxDuration?: number
}): AlmightyValidationIssue | null => {
    if (nextCount > maxCount) return 'video-max-count'
    if (
        duration !== undefined &&
        (duration < minDuration || duration > maxDuration)
    ) {
        return 'video-duration-range'
    }
    if (
        nextTotalDuration !== undefined &&
        nextTotalDuration > totalMaxDuration
    ) {
        return 'video-total-duration'
    }
    return null
}

export const validateAlmightyAudioLimits = ({
    nextCount,
    duration,
    nextTotalDuration,
    maxCount = ALMIGHTY_AUDIO_MAX_COUNT,
    minDuration = ALMIGHTY_AUDIO_MIN_DURATION,
    maxDuration = ALMIGHTY_AUDIO_MAX_DURATION,
    totalMaxDuration = ALMIGHTY_AUDIO_TOTAL_MAX_DURATION,
}: {
    nextCount: number
    duration?: number
    nextTotalDuration?: number
    maxCount?: number
    minDuration?: number
    maxDuration?: number
    totalMaxDuration?: number
}): AlmightyValidationIssue | null => {
    if (nextCount > maxCount) return 'audio-max-count'
    if (
        duration !== undefined &&
        (duration < minDuration || duration > maxDuration)
    ) {
        return 'audio-duration-range'
    }
    if (
        nextTotalDuration !== undefined &&
        nextTotalDuration > totalMaxDuration
    ) {
        return 'audio-total-duration'
    }
    return null
}

export const validateAlmightyCombination = ({
    audioCount,
    hasVisualReference,
}: {
    audioCount: number
    hasVisualReference: boolean
}): AlmightyValidationIssue | null => {
    if (audioCount > 0 && !hasVisualReference) {
        return 'audio-needs-visual-reference'
    }
    if (!hasVisualReference) return 'reference-required'
    return null
}

export const getAlmightyValidationMessage = (
    issue: AlmightyValidationIssue,
    locale?: string,
    context: AlmightyValidationMessageContext = {}
) => {
    const zh = isZhLocale(locale)
    switch (issue) {
        case 'image-max-count':
            return zh
                ? `图片参考最多上传 ${context.imageMaxCount ?? ALMIGHTY_IMAGE_MAX_COUNT} 张`
                : `You can upload up to ${context.imageMaxCount ?? ALMIGHTY_IMAGE_MAX_COUNT} image references`
        case 'video-max-count':
            return zh
                ? `视频参考最多上传 ${context.videoMaxCount ?? ALMIGHTY_VIDEO_MAX_COUNT} 段`
                : `You can upload up to ${context.videoMaxCount ?? ALMIGHTY_VIDEO_MAX_COUNT} video references`
        case 'video-duration-range':
            return zh
                ? `单段视频参考时长需在 ${context.minDuration ?? ALMIGHTY_VIDEO_MIN_DURATION}-${context.maxDuration ?? ALMIGHTY_VIDEO_MAX_DURATION}s 之间`
                : `Each video reference must be ${context.minDuration ?? ALMIGHTY_VIDEO_MIN_DURATION}-${context.maxDuration ?? ALMIGHTY_VIDEO_MAX_DURATION}s`
        case 'video-total-duration':
            return zh
                ? `视频参考总时长不能超过 ${context.totalMaxDuration ?? ALMIGHTY_VIDEO_TOTAL_MAX_DURATION}s`
                : `Total video reference duration must be within ${context.totalMaxDuration ?? ALMIGHTY_VIDEO_TOTAL_MAX_DURATION}s`
        case 'audio-max-count':
            return zh
                ? `音频参考最多上传 ${context.audioMaxCount ?? ALMIGHTY_AUDIO_MAX_COUNT} 段`
                : `You can upload up to ${context.audioMaxCount ?? ALMIGHTY_AUDIO_MAX_COUNT} audio references`
        case 'audio-duration-range':
            return zh
                ? `单段音频参考时长需在 ${context.minDuration ?? ALMIGHTY_AUDIO_MIN_DURATION}-${context.maxDuration ?? ALMIGHTY_AUDIO_MAX_DURATION}s 之间`
                : `Each audio reference must be ${context.minDuration ?? ALMIGHTY_AUDIO_MIN_DURATION}-${context.maxDuration ?? ALMIGHTY_AUDIO_MAX_DURATION}s`
        case 'audio-total-duration':
            return zh
                ? `音频参考总时长不能超过 ${context.totalMaxDuration ?? ALMIGHTY_AUDIO_TOTAL_MAX_DURATION}s`
                : `Total audio reference duration must be within ${context.totalMaxDuration ?? ALMIGHTY_AUDIO_TOTAL_MAX_DURATION}s`
        case 'audio-needs-visual-reference':
            return zh
                ? '上传音频参考前，至少需要保留一张图片或一段视频参考'
                : 'Audio references require at least one image or video reference'
        case 'reference-required':
            return zh
                ? '请至少上传一张图片或一段视频参考素材'
                : 'Please upload at least one image or video reference'
    }
}

export const getAlmightyMediaReadErrorMessage = (
    error: unknown,
    locale?: string
) => {
    const zh = isZhLocale(locale)
    const code =
        error instanceof AlmightyMediaReadError
            ? error.code
            : typeof (error as any)?.message === 'string'
              ? (error as any).message
              : ''

    switch (code) {
        case 'video-read-timeout':
            return zh
                ? '读取视频元数据超时，请更换素材后重试'
                : 'Timed out while reading video metadata'
        case 'video-invalid-duration':
            return zh
                ? '无法识别该视频时长，请更换素材后重试'
                : 'Invalid video duration'
        case 'video-read-failed':
            return zh ? '视频读取失败，请更换素材后重试' : 'Failed to read video'
        case 'audio-read-timeout':
            return zh
                ? '读取音频元数据超时，请更换素材后重试'
                : 'Timed out while reading audio metadata'
        case 'audio-invalid-duration':
            return zh
                ? '无法识别该音频时长，请更换素材后重试'
                : 'Invalid audio duration'
        case 'audio-read-failed':
            return zh ? '音频读取失败，请更换素材后重试' : 'Failed to read audio'
        case 'image-read-failed':
            return zh ? '图片读取失败，请更换素材后重试' : 'Failed to read image'
        default:
            return zh ? '上传失败，请稍后重试' : 'Upload failed, please try again'
    }
}

export const readAlmightyVideoMetadata = async (
    source: File | string
): Promise<{ duration: number; width: number; height: number }> => {
    if (!import.meta.client) {
        return { duration: 0, width: 0, height: 0 }
    }

    return await new Promise((resolve, reject) => {
        const video = document.createElement('video')
        video.preload = 'metadata'
        video.muted = true
        ;(video as any).playsInline = true
        video.crossOrigin = 'anonymous'

        const { src, revoke } = createMediaSource(source)
        let settled = false
        let seekAttempted = false

        const resolveIfReady = () => {
            const duration = normalizeFiniteDuration(video.duration)
            if (!isFinitePositiveDuration(duration)) return false

            settled = true
            window.clearTimeout(timeout)
            cleanupMediaElement(video, revoke)
            resolve({
                duration,
                width: Number.isFinite(video.videoWidth) ? video.videoWidth : 0,
                height: Number.isFinite(video.videoHeight)
                    ? video.videoHeight
                    : 0,
            })
            return true
        }

        const rejectWith = (code: AlmightyMediaReadErrorCode) => {
            if (settled) return
            settled = true
            window.clearTimeout(timeout)
            cleanupMediaElement(video, revoke)
            reject(new AlmightyMediaReadError(code))
        }

        const timeout = window.setTimeout(() => {
            rejectWith('video-read-timeout')
        }, ALMIGHTY_MEDIA_METADATA_TIMEOUT)

        video.onloadedmetadata = () => {
            if (resolveIfReady()) return
            if (video.duration === Infinity && !seekAttempted) {
                seekAttempted = true
                try {
                    video.currentTime = 1e101
                    return
                } catch {
                    // ignore
                }
            }
        }
        video.ondurationchange = () => {
            resolveIfReady()
        }
        video.onloadeddata = () => {
            resolveIfReady()
        }
        video.onseeked = () => {
            if (resolveIfReady()) return
            rejectWith('video-invalid-duration')
        }
        video.onerror = () => {
            rejectWith('video-read-failed')
        }

        video.src = src
        video.load()
    })
}

export const readAlmightyAudioDuration = async (
    source: File | string
): Promise<number> => {
    if (!import.meta.client) return 0

    return await new Promise((resolve, reject) => {
        const audio = new Audio()
        audio.preload = 'metadata'
        audio.crossOrigin = 'anonymous'

        const { src, revoke } = createMediaSource(source)
        let settled = false

        const resolveIfReady = () => {
            const duration = normalizeFiniteDuration(audio.duration)
            if (!isFinitePositiveDuration(duration)) return false

            settled = true
            window.clearTimeout(timeout)
            cleanupMediaElement(audio, revoke)
            resolve(duration)
            return true
        }

        const rejectWith = (code: AlmightyMediaReadErrorCode) => {
            if (settled) return
            settled = true
            window.clearTimeout(timeout)
            cleanupMediaElement(audio, revoke)
            reject(new AlmightyMediaReadError(code))
        }

        const timeout = window.setTimeout(() => {
            rejectWith('audio-read-timeout')
        }, ALMIGHTY_MEDIA_METADATA_TIMEOUT)

        audio.onloadedmetadata = () => {
            resolveIfReady()
        }
        audio.ondurationchange = () => {
            resolveIfReady()
        }
        audio.onloadeddata = () => {
            resolveIfReady()
        }
        audio.onerror = () => {
            rejectWith('audio-read-failed')
        }

        audio.src = src
        audio.load()
    })
}
